import { useMemo } from 'react';

import { TreeNode } from '@work4all/components/lib/dataDisplay/tree';

import { useDataProvider } from '@work4all/data';

import { Category } from '@work4all/models/lib/Classes/Category.entity';
import { CategoryClass } from '@work4all/models/lib/Classes/CategoryClass.entity';
import { DataRequest, Filters } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

export const useCategoryClasses = (
  filter: Filters | null,
  entity?: Entities
): {
  treeData: TreeNode[];
  originalById: Map<string, Category | CategoryClass>;
  categoryClasses: CategoryClass[];
  refetch: () => Promise<void>;
} => {
  const requestData: DataRequest = {
    entity: entity ?? Entities.categoryClass,
    data: CATEGORY_CLASS_FIELDS,
    filter,
  };

  const response = useDataProvider<CategoryClass>(requestData);

  const { data: categoryClasses } = response;

  const { treeData, originalById } = useMemo(() => {
    const originalById = new Map<string, CategoryClass | Category>();

    // TODO The categories returned from the API are not sorted. You can sort
    // the root category classes by providing the sort options, but the nested
    // categories are not sorted anyway. For now we just sort all the items
    // client-side.
    function compareCategoriesByName(
      a: Category | CategoryClass,
      b: Category | CategoryClass
    ) {
      return a.name.localeCompare(b.name);
    }

    function mapTreeItem(item: CategoryClass | Category): TreeNode {
      const id = item.id.toString();

      const treeItem: TreeNode = {
        id,
        label: item.name,
        children: isCategoryClass(item)
          ? [...item.categoryList]
              .sort(compareCategoriesByName)
              .map(mapTreeItem)
          : null,
      };

      originalById.set(id, item);

      return treeItem;
    }

    const treeData = [...categoryClasses]
      .sort(compareCategoriesByName)
      .map(mapTreeItem);

    return { treeData, originalById };
  }, [categoryClasses]);

  return { treeData, originalById, categoryClasses, refetch: response.refetch };
};

export const isCategoryClass = (
  item: CategoryClass | Category
): item is CategoryClass => {
  return 'categoryList' in item;
};

export const getCategoryFilter = (entity: Entities) => {
  switch (entity) {
    case Entities.customer:
      return [
        {
          $and: [
            { isCustomerCategory: { $eq: true } },
            { isPersonCategory: { $eq: false } },
          ],
        },
      ];
    case Entities.supplier:
      return [
        {
          $and: [
            { isSupplierCategory: { $eq: true } },
            { isPersonCategory: { $eq: false } },
          ],
        },
      ];
    default:
      return [];
  }
};

const CATEGORY_CLASS_FIELDS: CategoryClass = {
  id: null,
  name: null,
  categoryList: [
    {
      id: null,
      name: null,
    },
  ],
};
